import { useCallback, RefObject } from 'react';
import { HandleOpenProps } from '../popover.types';
import { UsePopoverTimeoutsReturn } from './use-popover-timeouts';
import { UseOpenAutoFocusReturn } from './use-open-auto-focus';
import { UseRefocusTriggerOnCloseReturn } from './use-refocus-trigger-on-close';

export type UsePopoverInteractionHandlersParams = {
  onOpen?: () => void;
  onClose?: () => void;
  previousState: RefObject<'open' | 'closed'>;
  setOpen: (value: boolean) => void;
  setClosing: (value: boolean) => void;
  setDisplay: (value: string) => void;
  setMountContent: (value: boolean) => void;
  setHasOpenedAtLeastOnce: (value: boolean) => void;
  timeouts: UsePopoverTimeoutsReturn;
  autoFocus: UseOpenAutoFocusReturn;
  refocusTrigger: UseRefocusTriggerOnCloseReturn;
};

export type UsePopoverInteractionHandlersReturn = {
  handleOpen: (meta?: HandleOpenProps) => void;
  handleClose: () => void;
};

/**
 * Consolidates handleOpen and handleClose logic with all dependencies.
 * This is the orchestration layer that ties together state, timeouts, and focus management.
 */
export function usePopoverInteractionHandlers({
  onOpen,
  onClose,
  previousState,
  setOpen,
  setClosing,
  setDisplay,
  setMountContent,
  setHasOpenedAtLeastOnce,
  timeouts,
  autoFocus,
  refocusTrigger,
}: UsePopoverInteractionHandlersParams): UsePopoverInteractionHandlersReturn {
  const handleOpen = useCallback(
    (meta?: HandleOpenProps) => {
      previousState.current = 'open';
      setHasOpenedAtLeastOnce(true);
      onOpen?.();

      // Clear any pending timeouts
      timeouts.clearAllTimeouts();

      setMountContent(true);
      setClosing(false);
      setOpen(true);

      // Timeout lets us briefly delay setting the display so we still get the open slide transition animations
      timeouts.scheduleDisplayBlock(() => {
        setDisplay('block');
      });

      // If opening via arrow keys, we want to immediately focus content:
      // - wait for popover to open so content is mounted and focusable
      // - if up arrow key, focus the last focusable element
      // - if down arrow key, focus the first focusable element
      if (meta?.keyDirection) {
        autoFocus.handleAutoFocus(meta.keyDirection);
      }
    },
    [
      onOpen,
      previousState,
      setHasOpenedAtLeastOnce,
      setMountContent,
      setClosing,
      setOpen,
      setDisplay,
      timeouts,
      autoFocus,
    ]
  );

  const handleClose = useCallback(() => {
    previousState.current = 'closed';
    onClose?.();
    setClosing(true);

    // Clear display block timer if it's pending
    timeouts.clearAllTimeouts();

    timeouts.scheduleClose(() => {
      setOpen(false);
      setClosing(false);
    });

    setDisplay('none');

    timeouts.scheduleOpen(() => {
      setMountContent(false);
    });

    refocusTrigger.refocusTrigger();
  }, [previousState, onClose, setClosing, setOpen, setDisplay, setMountContent, timeouts, refocusTrigger]);

  return {
    handleOpen,
    handleClose,
  };
}
